// Turning a game state into JSON and back.
//
// **Structural, not schema-driven.** There is no list of fields here, and that
// is the whole design: a `GameState` is a deep tree of Maps, Sets, typed
// arrays and plain objects, and a hand-written serialiser would be a second
// copy of that shape — so the day somebody adds a field to `IndustryState`,
// the save quietly stops carrying it and the bug surfaces as a loaded game
// that is subtly wrong. This walks whatever is there.
//
// The tag names are short because they appear once per container in a file
// that lives in a browser's storage quota. They are also *versioned by the
// caller* rather than here: this is a codec, and what a valid state looks like
// is somebody else's question.
//
// The guarantee is checked rather than asserted: `codec.test.ts` round-trips a
// deliberately rich state and compares `hashState`, which covers every field
// the game actually reads. A forgotten container fails that test rather than
// shipping.

type Encoded =
  | { m: [unknown, unknown][] }
  | { s: unknown[] }
  | { a: TypedName; v: number[] }
  | unknown;

type TypedName = "i8" | "u8" | "i16" | "u16" | "i32" | "u32" | "f32" | "f64";

const TYPED: Record<TypedName, new (values: number[]) => ArrayLike<number>> = {
  i8: Int8Array,
  u8: Uint8Array,
  i16: Int16Array,
  u16: Uint16Array,
  i32: Int32Array,
  u32: Uint32Array,
  f32: Float32Array,
  f64: Float64Array
};

const NAME_OF = new Map<unknown, TypedName>([
  [Int8Array, "i8"],
  [Uint8Array, "u8"],
  [Int16Array, "i16"],
  [Uint16Array, "u16"],
  [Int32Array, "i32"],
  [Uint32Array, "u32"],
  [Float32Array, "f32"],
  [Float64Array, "f64"]
]);

/** Walk a value into something `JSON.stringify` will not lose. */
export function encode(value: unknown): Encoded {
  if (value === null || typeof value !== "object") return value;

  if (value instanceof Map) {
    // Entries, not an object: the keys here are *numbers* — squares — and an
    // object would stringify every one of them and hand them back as strings.
    return { m: [...value].map(([k, v]) => [encode(k), encode(v)]) };
  }
  if (value instanceof Set) return { s: [...value].map(encode) };

  const typed = NAME_OF.get(value.constructor);
  if (typed) return { a: typed, v: Array.from(value as ArrayLike<number>) };

  if (Array.isArray(value)) return value.map(encode);

  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(value)) out[key] = encode(entry);
  return out;
}

/** The inverse. Anything this does not recognise comes back as it went in. */
export function decode(value: unknown): unknown {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map(decode);

  const tagged = value as Record<string, unknown>;
  if (Array.isArray(tagged.m)) {
    return new Map(
      (tagged.m as [unknown, unknown][]).map(([k, v]) => [decode(k), decode(v)])
    );
  }
  if (Array.isArray(tagged.s)) return new Set((tagged.s as unknown[]).map(decode));
  if (typeof tagged.a === "string" && Array.isArray(tagged.v)) {
    const make = TYPED[tagged.a as TypedName];
    // An unknown array kind is a save from a future build. Better to fail the
    // whole load than to hand the rules a plain array where they expect an
    // `Int32Array` — `kings.fill(-1)` on the wrong type is a silent no-op.
    if (!make) throw new Error(`unknown typed array: ${tagged.a}`);
    return new make(tagged.v as number[]);
  }

  const out: Record<string, unknown> = {};
  for (const [key, entry] of Object.entries(tagged)) out[key] = decode(entry);
  return out;
}
